/**
 * Kiosko y productos — esquemas del contrato (AF §9.4).
 *
 * Tres reglas viven en la FORMA del dato, no en un `if`:
 * - **RN-CON-24**: no existe «transferir stock entre sedes». No hay esquema para eso, y
 *   `stockMovementKind` no tiene el valor: la operación no se puede ni pedir.
 * - **RN-CON-25**: la venta no cierra sin pago. `createSaleBody` lleva el medio de pago
 *   obligatorio — no hay «venta a cuenta».
 * - **RN-CON-29**: el conteo físico NO ajusta. `submitStockCountBody` sólo trae lo
 *   contado; el ajuste es otro endpoint, con otro rol y motivo obligatorio.
 */
import { z } from "zod";
import { TARGET_MARGIN_MAX_PCT } from "../kiosk-margins.js";
import { PAYMENT_METHODS, PERSON_KINDS, SALE_CHANNELS, SALE_STATUSES, STOCK_COUNT_STATUSES, STOCK_MOVEMENT_KINDS, } from "../enums.js";
import { dateISO, dateOnly, listQuery, moneySchema, objectId, reasonRequired, yearMonth, } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Catálogo de productos (RN-CON-23)
// ─────────────────────────────────────────────────────────────────────────────
/** Precio **único para las tres sedes** (rel. 11.5): no hay precio por sede en el esquema. */
export const createProductBody = z.object({
    name: z.string().trim().min(2).max(120),
    category: z.string().trim().min(2).max(60),
    barcode: z.string().trim().min(4).max(40).optional(),
    photoKey: z.string().trim().max(300).optional(),
    price: moneySchema.int().min(0),
    tracksExpiry: z.boolean().default(false),
    /** Stock mínimo por sede: lo define control de stock (RN-CON-30). */
    minStockByBranch: z
        .array(z.object({ branchId: objectId, minStock: z.number().int().min(0) }))
        .max(10)
        .optional(),
    /**
     * Margen que el dueño le quiere sacar al producto, en % (0 a 95, con un decimal). `null`
     * = sin objetivo: no hay aviso de margen bajo.
     */
    targetMarginPct: z
        .number()
        .min(0, "El margen objetivo va de 0 a 95 %")
        .max(TARGET_MARGIN_MAX_PCT, "El margen objetivo va de 0 a 95 %")
        .nullable()
        .optional(),
    /**
     * Guardar un precio por debajo del último costo: sin esto la API contesta
     * `409 PRICE_BELOW_COST`. Con `true` exige `belowCostReason`, que queda auditado.
     */
    confirmBelowCost: z.boolean().optional(),
    belowCostReason: z.string().trim().max(500).optional(),
});
export const updateProductBody = createProductBody.partial().extend({
    active: z.boolean().optional(),
    /** RN-CON-23: cambiar el precio es una acción auditada; el motivo queda en el log. */
    priceChangeReason: z.string().trim().max(500).optional(),
});
/** `true`/`false` del querystring: `z.coerce.boolean()` leería `"false"` como verdadero. */
const queryBoolean = z.union([
    z.boolean(),
    z.enum(["true", "false"]).transform((v) => v === "true"),
]);
export const PRODUCT_STOCK_FILTERS = ["all", "low", "out"];
export const PRODUCT_SORTS = ["name", "price", "margin", "stock"];
export const listProductsQuery = listQuery.extend({
    category: z.string().trim().max(60).optional(),
    active: z.coerce.boolean().optional(),
    /** Sólo los que perforan su mínimo en la sede (RN-CON-30). */
    belowMin: z.coerce.boolean().optional(),
    barcode: z.string().trim().max(40).optional(),
    /**
     * Sólo los que tienen stock (> 0) en la sede: es lo que lista «Vender», igual que la
     * tablet. Se filtra ANTES de paginar: filtrar en la pantalla después de traer una página
     * del catálogo entero escondía productos con stock que caían fuera de esa página.
     */
    inStock: queryBoolean.optional(),
    /** Sólo los que se le compran a este proveedor del kiosko (vínculo activo). */
    supplierId: objectId.optional(),
    /**
     * Estado del stock en la sede (`branchId` o la activa): `low` = en o bajo su mínimo,
     * `out` = sin stock (cero, o el producto no está dado de alta en la sede).
     */
    stock: z.enum(PRODUCT_STOCK_FILTERS).optional(),
    /** Orden del listado. `margin` deja al final los que no tienen margen calculable. */
    sort: z.enum(PRODUCT_SORTS).default("name"),
    dir: z.enum(["asc", "desc"]).default("asc"),
});
// ─────────────────────────────────────────────────────────────────────────────
// Proveedores del kiosko (23-oct-2026)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * CUIT del proveedor del kiosko: se aceptan guiones, puntos y espacios al tipear y se
 * guarda en 11 dígitos. El largo y el dígito verificador los valida el backend, que
 * contesta con un mensaje que dice qué está mal. `""` = sin CUIT (o borrarlo al editar).
 */
const kioskSupplierCuit = z
    .string()
    .trim()
    .max(20)
    .transform((v) => v.replace(/[-\s.]/g, ""));
/**
 * Proveedor **del kiosko**: es otra entidad que los proveedores del gimnasio
 * (`createSupplierBody`). El kiosko es un negocio aparte y sus proveedores no se mezclan.
 */
export const createKioskSupplierBody = z.object({
    businessName: z.string().trim().min(2).max(200),
    cuit: kioskSupplierCuit.optional(),
    contactName: z.string().trim().max(120).optional(),
    phone: z.string().trim().max(40).optional(),
    email: z.union([z.literal(""), z.string().trim().email("El mail no es válido")]).optional(),
    notes: z.string().trim().max(2000).optional(),
});
/** Edición. `active: false` lo da de baja: nada se borra. */
export const updateKioskSupplierBody = createKioskSupplierBody.partial().extend({
    active: z.boolean().optional(),
});
export const listKioskSuppliersQuery = listQuery.extend({
    active: queryBoolean.optional(),
});
/**
 * Vincular un producto con un proveedor del kiosko: el código con el que el proveedor lo
 * lista y el último precio de costo. `lastCost: null` = no se sabe (no es $0). Con
 * `preferred: true` pasa a ser EL preferido del producto (sólo puede haber uno).
 */
export const linkProductSupplierBody = z.object({
    kioskSupplierId: objectId,
    supplierCode: z.string().trim().max(60).optional(),
    lastCost: moneySchema.min(0).nullable().optional(),
    preferred: z.boolean().optional(),
});
/** Editar el vínculo. `active: false` lo desvincula (y deja de ser preferido). */
export const updateProductSupplierBody = z.object({
    supplierCode: z.string().trim().max(60).optional(),
    lastCost: moneySchema.min(0).nullable().optional(),
    preferred: z.boolean().optional(),
    active: z.boolean().optional(),
});
export const productSupplierParams = z.object({ id: objectId, linkId: objectId });
// ─────────────────────────────────────────────────────────────────────────────
// Compras a proveedores del kiosko (23-oct-2026)
//
// Usan SÓLO los proveedores del kiosko (`kioskSuppliers`) y la caja del kiosko: el kiosko
// es un negocio aparte y sus compras no pasan por Egresos ni por la caja del gimnasio.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Cómo se pagó la compra. `cash_kiosk` sale del efectivo de la caja del kiosko de la sede
 * (exige el turno abierto y queda como egreso en él); el resto no toca ninguna caja.
 */
export const KIOSK_PURCHASE_PAYMENT_METHODS = [
    "cash_kiosk",
    "transfer",
    "card",
    "account",
];
export const KIOSK_PURCHASE_STATUSES = ["confirmed", "voided"];
export const createKioskPurchaseBody = z.object({
    kioskSupplierId: objectId,
    /** La sede donde entra la mercadería: el stock es por sede (RN-CON-24). */
    branchId: objectId,
    /** Fecha de la compra (la del remito o factura). Si no viene, ahora. */
    date: dateISO.optional(),
    /** Comprobante del proveedor (factura, remito), opcional. */
    voucher: z
        .object({
        type: z.string().trim().min(1).max(40),
        number: z.string().trim().min(1).max(60),
    })
        .optional(),
    items: z
        .array(z.object({
        productId: objectId,
        qty: z.number().int().positive().max(100_000),
        /** Costo unitario en centavos. Un costo 0 no es un costo: «sin dato» no se registra. */
        unitCost: moneySchema.positive("El costo unitario tiene que ser mayor a cero"),
        /** Vencimiento del lote, para los productos que lo controlan (RN-CON-28). */
        lotExpiry: dateISO.optional(),
    }))
        .min(1)
        .max(100),
    paymentMethod: z.enum(KIOSK_PURCHASE_PAYMENT_METHODS),
    /**
     * Clave que la pantalla genera al abrir «Nueva compra»: el doble click o el reintento
     * de una red lenta devuelven la misma compra en vez de duplicar stock y egreso.
     */
    idempotencyKey: z.string().trim().min(8).max(80).optional(),
});
export const listKioskPurchasesQuery = listQuery.extend({
    kioskSupplierId: objectId.optional(),
    productId: objectId.optional(),
    status: z.enum(KIOSK_PURCHASE_STATUSES).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** Anular una compra: motivo obligatorio (D5). Resta lo comprado y devuelve el efectivo. */
export const voidKioskPurchaseBody = z.object({ reason: reasonRequired });
/** Historial de precios de compra: por producto, por proveedor del kiosko, o los dos. */
export const kioskPurchasePriceHistoryQuery = z
    .object({
    productId: objectId.optional(),
    kioskSupplierId: objectId.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
})
    .refine((q) => Boolean(q.productId || q.kioskSupplierId), {
    message: "Indicá el producto o el proveedor del kiosko",
});
/** Stock mínimo por sede: rol control de stock (RN-CON-30). */
export const setMinStockBody = z.object({
    branchId: objectId,
    minStock: z.number().int().min(0),
});
// ─────────────────────────────────────────────────────────────────────────────
// Stock (RN-CON-24, RN-CON-28/29)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Los movimientos que un humano puede PEDIR. La compra la genera la aprobación del
 * egreso (RN-CON-16) y la venta la genera la venta: ninguna de las dos se carga a mano.
 */
export const manualStockMovementKind = z.enum(["shrinkage", "adjustment"]);
/** Merma con motivo obligatorio: «vencido», rotura… (RN-CON-28, D5). */
export const registerShrinkageBody = z.object({
    productId: objectId,
    qty: z.number().int().positive(),
    reason: reasonRequired,
    /** Vencimiento del lote que se da de baja, cuando el producto lo controla. */
    lotExpiry: dateISO.optional(),
});
export const listStockMovementsQuery = listQuery.extend({
    productId: objectId.optional(),
    kind: z.enum(STOCK_MOVEMENT_KINDS).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/**
 * **Consumo interno**: el dueño o el staff retiran productos del kiosko a su nombre.
 * Baja el stock de la sede activa (RN-CON-24: el stock es por sede) y NO es venta, cobro,
 * egreso ni factura. Lo registra recepción en adelante, en nombre de `takenByUserId`.
 */
export const internalConsumptionBody = z.object({
    items: z
        .array(z.object({ productId: objectId, qty: z.number().int().positive().max(999) }))
        .min(1)
        .max(50),
    /** Quién se lleva la mercadería: cualquier usuario interno activo (de cualquier sede). */
    takenByUserId: objectId,
    /** Motivo corto y obligatorio («consumo propio», «reunión de staff»…). */
    reason: z.string().trim().min(3, "El motivo es obligatorio").max(200),
    note: z.string().trim().max(1000).optional(),
});
/** Reporte de consumo interno: por persona y producto, del mes. */
export const internalConsumptionReportQuery = z.object({
    month: yearMonth.optional(),
    branchId: objectId.optional(),
    takenByUserId: objectId.optional(),
});
/**
 * **Conteo físico** (RN-CON-29): la recepcionista carga lo contado y nada más. El
 * esperado y la diferencia los calcula el backend — que el cliente mande el «esperado»
 * sería dejarle elegir contra qué se compara.
 */
export const submitStockCountBody = z.object({
    items: z
        .array(z.object({ productId: objectId, counted: z.number().int().min(0) }))
        .min(1)
        .max(500),
    note: z.string().trim().max(1000).optional(),
});
/** El ajuste lo aprueba control de stock, con motivo (RN-CON-29). */
export const adjustStockCountBody = z.object({
    reason: reasonRequired,
    /** Sin lista, se ajustan todas las líneas con diferencia. */
    productIds: z.array(objectId).max(500).optional(),
});
export const dismissStockCountBody = z.object({ reason: reasonRequired });
export const listStockCountsQuery = listQuery.extend({
    status: z.enum(STOCK_COUNT_STATUSES).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** Lotes por vencer: la pantalla de stock y el aviso a control de stock (RN-CON-28). */
export const expiringLotsQuery = z.object({
    branchId: objectId.optional(),
    /** Si no viene, `stock.expiryAlertDays` (default 15, PV-74). */
    days: z.coerce.number().int().min(1).max(365).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Ventas (RN-CON-25..27)
// ─────────────────────────────────────────────────────────────────────────────
export const saleStatus = z.enum(SALE_STATUSES);
/** Medios que el kiosko acepta (RN-CAJ-02): sin `payway_recurring` ni `payway_online`. */
export const kioskPaymentMethod = z.enum(["cash", "mp_point", "mp_qr", "payway_terminal"]);
export const saleItemInput = z.object({
    productId: objectId,
    qty: z.number().int().positive().max(999),
});
/**
 * **La venta se cobra al entregar** (RN-CON-25, rel. 11.3): `method` es obligatorio y no
 * existe un estado «a pagar». El socio es OPCIONAL y nunca frena la fila (RN-CON-26).
 */
export const createSaleBody = z.object({
    items: z.array(saleItemInput).min(1).max(50),
    method: kioskPaymentMethod,
    customer: z.object({ kind: z.enum(PERSON_KINDS), id: objectId }).optional(),
    /**
     * Sólo efectivo: la caja del kiosko abierta de la sede. Si no viene, se toma esa. Los
     * medios electrónicos no van a ninguna caja de efectivo.
     */
    shiftId: objectId.optional(),
    /** MP Point: id del dispositivo. QR: se genera dinámico. */
    pointDeviceId: z.string().trim().max(80).optional(),
    /** Posnet Payway: la intención ya aprobada de Caja (RN-CAJ-16). */
    terminalIntentId: z.string().trim().max(80).optional(),
    /** Pedir factura en el acto aunque sea efectivo (RN-CON-27). */
    invoice: z
        .object({
        letter: z.enum(["A", "B"]).default("B"),
        cuit: z
            .string()
            .trim()
            .regex(/^\d{11}$/)
            .optional(),
        name: z.string().trim().max(200).optional(),
    })
        .optional(),
});
/**
 * **Anulación del día** (caso borde 4): rol y motivo, NC si estaba facturada y el stock
 * vuelve a entrar.
 */
export const voidSaleBody = z.object({ reason: reasonRequired });
export const listSalesQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    status: saleStatus.optional(),
    productId: objectId.optional(),
    memberId: objectId.optional(),
    shiftId: objectId.optional(),
    userId: objectId.optional(),
    /** Medio del cobro de la venta (efectivo, QR, Point, posnet). */
    method: kioskPaymentMethod.optional(),
    /** Mostrador atendido o tablet pública. */
    channel: z.enum(SALE_CHANNELS).optional(),
});
/** Reportes de kiosko (RN-CON-31): ventas, unidades, margen, mermas y diferencias. */
export const kioskReportQuery = z.object({
    month: yearMonth.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    branchId: objectId.optional(),
    groupBy: z.enum(["product", "branch", "category", "day"]).default("product"),
});
/**
 * Estadísticas de ventas del kiosko (VTCS-120). `from`/`to` son **días operativos
 * argentinos**, los dos inclusive: «del 1 al 31» es el mes entero, sin que la zona horaria
 * se coma la última noche. Sin fechas, el mes en curso.
 *
 * `branchId: "all"` pide todas las sedes que el usuario puede ver; sin `branchId`, la
 * sede activa.
 */
export const kioskSalesStatsQuery = z
    .object({
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    branchId: z.union([objectId, z.literal("all")]).optional(),
    method: kioskPaymentMethod.optional(),
    groupBy: z.enum(["day", "week", "month"]).default("day"),
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "La fecha «desde» no puede ser posterior a «hasta»",
    path: ["to"],
});
// ─────────────────────────────────────────────────────────────────────────────
// Tablet kiosko (RN-CON-25, PV-98)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * La tablet es un **puesto atendido**: además de la api key del dispositivo, cada venta
 * lleva el operador y su PIN. El socio nunca opera la tablet solo (PV-98).
 */
export const kioskOperator = z.object({
    userId: objectId,
    pin: z
        .string()
        .trim()
        .regex(/^\d{4,8}$/, "El PIN son 4 a 8 dígitos"),
});
export const kioskSaleBody = createSaleBody.extend({ operator: kioskOperator });
export const kioskVoidSaleBody = voidSaleBody.extend({ operator: kioskOperator });
/** Apertura de la caja del kiosko desde la tablet: fondo contado + operador con PIN. */
export const kioskRegisterOpenBody = z.object({
    openingDeclaredCash: moneySchema.min(0),
    operator: kioskOperator,
});
/** Cierre de la caja del kiosko desde la tablet: lo contado, a ciegas, con el operador. */
export const kioskRegisterCloseBody = z.object({
    declaredCash: moneySchema.min(0),
    /** Lo que queda en la caja como fondo del turno siguiente; el resto va a un sobre. */
    remainder: moneySchema.min(0),
    note: z.string().trim().max(500).optional(),
    operator: kioskOperator,
});
/** «Pagar cuota» en la tablet: identifica al socio por QR o documento (AF §4 autopago). */
export const kioskLookupBody = z.object({
    docNumber: z.string().trim().max(20).optional(),
    memberNumber: z.coerce.number().int().positive().optional(),
    /** Token del carnet dinámico de la app (RN-ACC-06). */
    qr: z.string().trim().max(300).optional(),
});
/** Cobro de la cuota desde la tablet: MP Point o QR de MP, contra el turno vigente. */
export const kioskDuesPaymentBody = z.object({
    memberId: objectId,
    chargeIds: z.array(objectId).min(1).max(50),
    method: z.enum(["mp_point", "mp_qr"]),
    pointDeviceId: z.string().trim().max(80).optional(),
    operator: kioskOperator,
});
/** Estado de una intención de cobro del kiosko (MP Point / QR). */
export const kioskIntentQuery = z.object({ intentId: z.string().trim().min(4).max(120) });
/** Medios de pago del kiosko, exportado para la SPA. */
export const KIOSK_PAYMENT_METHODS = PAYMENT_METHODS.filter((m) => m === "cash" || m === "mp_point" || m === "mp_qr" || m === "payway_terminal");
//# sourceMappingURL=kiosko.js.map